import _ from 'lodash'
import { useMemo, useState } from 'react'

import { Z_INDEX_MAX } from '@/const/z-index'

import { ALL_PERMISSIONS, useUserPermissions } from '@/lib/app/check-permissions.hook'
import { cn } from '@/lib/ui/style-helpers'
import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'

import { CheckboxGroup } from '@/components/lemmala-design-system/checkbox-group/checkbox-group'
import { Modal } from '@/components/lemmala-design-system/modal/modal'

import { SET_PERMISSIONS_MODAL_TEXT } from './labels'

interface SetPermissionsModalProps {
  isOpen: boolean
  onClose: () => void
}

export const SetPermissionsModal = ({ isOpen, onClose }: SetPermissionsModalProps) => {
  const { urlParams, updateParam, removeParam } = useUrlParams()
  const userPermissions = useUserPermissions()

  const currentViewAsRole = urlParams[UrlParam.VIEW_AS_ROLE]
  const isUsingCustomPermissions = Boolean(currentViewAsRole)

  // Get current permissions (either custom or user's actual permissions)
  const currentPermissions = currentViewAsRole
    ? currentViewAsRole.split(',')
    : userPermissions || []

  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(currentPermissions)

  const permissionOptions = useMemo(
    () => ALL_PERMISSIONS.map((permission) => ({ label: permission, value: permission })),
    [],
  )

  const handleApply = () => {
    if (
      selectedPermissions.length > 0 &&
      !_.isEqual(_.sortBy(selectedPermissions), _.sortBy(userPermissions))
    ) {
      updateParam(UrlParam.VIEW_AS_ROLE, selectedPermissions.join(','))
    } else {
      removeParam(UrlParam.VIEW_AS_ROLE)
    }
    onClose()
  }

  const handleReset = () => {
    setSelectedPermissions(userPermissions)
    removeParam(UrlParam.VIEW_AS_ROLE)
    onClose()
  }

  const handleCancel = () => {
    setSelectedPermissions(currentPermissions)
    onClose()
  }

  return (
    <Modal
      title={SET_PERMISSIONS_MODAL_TEXT.TITLE}
      open={isOpen}
      size='medium'
      onCancel={handleCancel}
      onDismiss={handleCancel}
      onSecondary={handleReset}
      onOk={handleApply}
      showSecondaryButton
      showCancelButton={false}
      primaryButtonText={SET_PERMISSIONS_MODAL_TEXT.PRIMARY_BUTTON}
      secondaryButtonText={SET_PERMISSIONS_MODAL_TEXT.SECONDARY_BUTTON}
      style={{ zIndex: Z_INDEX_MAX }}
    >
      <div className='space-y-4'>
        <div className='rounded-md border border-main-border-default p-3'>
          <div className='flex items-center gap-2'>
            <div
              className={cn(
                'size-2 rounded-full',
                isUsingCustomPermissions
                  ? 'bg-main-foreground-warning'
                  : 'bg-main-foreground-success',
              )}
            />
            <span className='text-main-foreground-primary text-label-default'>
              {isUsingCustomPermissions
                ? SET_PERMISSIONS_MODAL_TEXT.STATUS_CUSTOM
                : SET_PERMISSIONS_MODAL_TEXT.STATUS_ACTUAL}
            </span>
          </div>
        </div>

        <CheckboxGroup
          label={SET_PERMISSIONS_MODAL_TEXT.AVAILABLE_PERMISSIONS_LABEL}
          options={permissionOptions}
          value={selectedPermissions}
          onChange={setSelectedPermissions}
          direction='vertical'
        />
      </div>
    </Modal>
  )
}
